import { Component, Injector, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AccessibilityService,
  AccessibilitySettings,
  ACCESSIBILITY_FEATURE_CARDS,
  FeatureCardConfig,
  ThemeMode,
  TextAlignMode,
  FontFamilyOption,
  CursorColorOption,
  StepLevel,
} from '../../services/accessibility.service';

@Component({
  selector: 'app-accessibility',
  standalone: false,
  templateUrl: './accessibility.component.html',
  styleUrl: './accessibility.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessibilityComponent implements OnInit, OnDestroy {

  // Constants — safe as field initializers, they don't depend on `this`.
  protected readonly cards: readonly FeatureCardConfig[] = ACCESSIBILITY_FEATURE_CARDS;
  protected readonly ThemeMode = ThemeMode;
  protected readonly TextAlignMode = TextAlignMode;
  protected readonly FontFamilyOption = FontFamilyOption;
  protected readonly CursorColorOption = CursorColorOption;
  protected readonly StepLevel = StepLevel;


  container:any= {};
  constructor(
         public injector: Injector,
         public route: ActivatedRoute,
         protected readonly accessibilityService: AccessibilityService,
    ) {
       this.container.isToggle = false;
    }
  ngOnInit(): void {
    // This component only exists while its hosting aside panel is open — see
    // the doc comment on AccessibilityService.isPanelOpen for why that makes
    // this lifecycle the real open/close signal.
    this.accessibilityService.openPanel();
  }

  ngOnDestroy(): void {
    this.accessibilityService.closePanel();
  }

  protected closePanel(): void {
    this.accessibilityService.closePanel();
  }

  protected onCardClick(card: FeatureCardConfig): void {
    if (card.controlType === 'toggle') {
      this.accessibilityService.toggleBoolean(card.key);
    }
  }

  protected onIncrement(card: FeatureCardConfig, event: Event): void {
    event.stopPropagation();
    this.accessibilityService.incrementStep(card.key, card.maxStep ?? StepLevel.Level5);
  }

  protected onDecrement(card: FeatureCardConfig, event: Event): void {
    event.stopPropagation();
    this.accessibilityService.decrementStep(card.key, card.minStep ?? StepLevel.Off);
  }

  protected getStepValue(key: keyof AccessibilitySettings): StepLevel {
    return this.accessibilityService.settings()[key] as StepLevel;
  }

  protected isCardActive(card: FeatureCardConfig): boolean {
  const value = this.accessibilityService.settings()[card.key];

  switch (card.controlType) {
    case 'toggle':
      return Boolean(value);
    case 'step':
      // Not `> Off` — several step cards (Brightness, Bigger Text, Text
      // Spacing, Zoom) allow negative values via minStep, and those are just
      // as much a change from default as a positive one.
      return (value as StepLevel) !== StepLevel.Off;
    case 'theme':
      return (value as ThemeMode) !== ThemeMode.Auto;
    case 'textAlign':
      return (value as TextAlignMode) !== TextAlignMode.Default;
    case 'fontFamily':
      return (value as FontFamilyOption) !== FontFamilyOption.Default;
    case 'cursorColor':
      return (value as CursorColorOption) !== CursorColorOption.Default;
    default:
      return false;
    }
  }

  protected resetAll(): void {
    this.accessibilityService.resetAll();
  }

  /** Drives the per-card corner badge: spinner while this card's change is being saved, checkmark once it's active/settled. */
  protected isCardSaving(card: FeatureCardConfig): boolean {
    return this.accessibilityService.isSaving() && this.accessibilityService.lastChangedKey() === card.key;
  }

  protected showCardBadge(card: FeatureCardConfig): boolean {
    return this.isCardActive(card) || this.isCardSaving(card);
  }
}
